import React from "react";
import ReactDOM from "react-dom";

// Branded "Magic Folio" landing. Shown when the URL has no client slug (bare
// "/") AND when a slug points at no client (unknown tenant) — both resolve to
// the same splash instead of a bare error with reload/home buttons.
// Portal to <body>: Stage's Canvas has a CSS transform, which would make our
// `position: fixed` resolve against the scaled 1920×1080 canvas instead of the
// window (transformed ancestors become the containing block for fixed
// descendants). Rendering into body escapes that so the gradient covers the
// whole window.
export default function NoTenant() {
  return ReactDOM.createPortal(
    <div className="no-tenant">
      <style>{`
        @import url(https://fonts.googleapis.com/css?family=Nunito:400,700,800);
        @keyframes nt-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-12px); }
        }
        @keyframes nt-drift {
          0% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(4%, -3%) scale(1.12); }
          100% { transform: translate(0, 0) scale(1); }
        }
        @keyframes nt-rise {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes nt-pulse {
          0%, 100% {
            text-shadow:
              0 0 6px rgba(57, 255, 20, 0.75),
              0 0 18px rgba(57, 255, 20, 0.45),
              0 0 34px rgba(57, 255, 20, 0.25),
              0 3px 6px rgba(0, 0, 0, 0.6);
          }
          50% {
            text-shadow:
              0 0 10px rgba(57, 255, 20, 0.95),
              0 0 26px rgba(57, 255, 20, 0.6),
              0 0 48px rgba(57, 255, 20, 0.35),
              0 3px 6px rgba(0, 0, 0, 0.6);
          }
        }
        .no-tenant {
          position: fixed;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          padding: 2rem;
          text-align: center;
          font-family: "Nunito", sans-serif;
          color: #fff;
          background:
            radial-gradient(circle at 22% 18%, rgba(57, 255, 20, 0.22), transparent 45%),
            radial-gradient(circle at 78% 30%, rgba(20, 130, 255, 0.22), transparent 45%),
            radial-gradient(circle at 82% 82%, rgba(57, 255, 20, 0.14), transparent 45%),
            radial-gradient(circle at 18% 85%, rgba(20, 130, 255, 0.16), transparent 48%),
            linear-gradient(160deg, #181818 0%, #141414 55%, #0d0d0d 100%);
        }
        .no-tenant__blob {
          position: absolute;
          border-radius: 50%;
          filter: blur(72px);
          opacity: 0.5;
          pointer-events: none;
        }
        .no-tenant__blob--a {
          width: 40vw; height: 40vw; top: -12%; left: -8%;
          background: radial-gradient(circle, #39ff14, transparent 70%);
          animation: nt-drift 14s ease-in-out infinite;
        }
        .no-tenant__blob--b {
          width: 45vw; height: 45vw; bottom: -16%; right: -10%;
          background: radial-gradient(circle, #1482ff, transparent 70%);
          animation: nt-drift 18s ease-in-out infinite reverse;
        }
        .no-tenant__blob--c {
          width: 38vw; height: 38vw; top: 20%; right: -6%;
          background: radial-gradient(circle, #35ff77, transparent 70%);
          animation: nt-drift 22s ease-in-out infinite;
        }
        .no-tenant__card {
          position: relative;
          z-index: 1;
          max-width: 520px;
          padding: 3rem 2.5rem;
          border-radius: 24px;
          background: rgba(255, 255, 255, 0.035);
          border: 1px solid rgba(57, 255, 20, 0.18);
          box-shadow: 0 30px 80px rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(14px);
          -webkit-backdrop-filter: blur(14px);
          animation: nt-rise 0.7s ease both;
        }
        .no-tenant__mark {
          width: 84px; height: 84px;
          margin: 0 auto 1.5rem;
          display: flex; align-items: center; justify-content: center;
          border-radius: 22px;
          font-size: 2.4rem; font-weight: 800;
          color: #39ff14;
          background: rgba(57, 255, 20, 0.08);
          border: 1px solid rgba(57, 255, 20, 0.4);
          box-shadow:
            0 0 24px rgba(57, 255, 20, 0.35),
            inset 0 0 18px rgba(57, 255, 20, 0.15);
          text-shadow: 0 0 12px rgba(57, 255, 20, 0.7);
          animation: nt-float 5s ease-in-out infinite;
        }
        .no-tenant__title {
          margin: 0;
          font-size: clamp(2.2rem, 6vw, 3.2rem);
          font-weight: 800;
          letter-spacing: 0.02em;
          color: #39ff14;
          animation: nt-pulse 3.5s ease-in-out infinite;
        }
        .no-tenant__cta {
          display: inline-block;
          margin-top: 2rem;
          padding: 0.85rem 2.6rem;
          border-radius: 999px;
          font-size: 1.05rem;
          font-weight: 800;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          text-decoration: none;
          color: #0d0d0d;
          background: linear-gradient(90deg, #39ff14, #35ff77);
          border: 1px solid rgba(57, 255, 20, 0.6);
          box-shadow: 0 0 22px rgba(57, 255, 20, 0.45);
          transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
        }
        .no-tenant__cta:hover {
          transform: translateY(-2px);
          filter: brightness(1.08);
          box-shadow: 0 0 32px rgba(57, 255, 20, 0.7);
        }
        .no-tenant__msg {
          margin: 1.1rem auto 0;
          max-width: 360px;
          font-size: 1.02rem;
          line-height: 1.55;
          color: rgba(255, 255, 255, 0.62);
        }
        .no-tenant__rule {
          width: 56px; height: 3px;
          margin: 1.6rem auto 0;
          border-radius: 3px;
          background: linear-gradient(90deg, #39ff14, #1482ff);
          box-shadow: 0 0 12px rgba(57, 255, 20, 0.5);
        }
        .no-tenant__credit {
          position: fixed;
          left: 0; right: 0; bottom: 1.4rem;
          z-index: 1;
          text-align: center;
          font-size: 0.85rem;
          color: rgba(255, 255, 255, 0.5);
        }
        .no-tenant__credit a {
          color: #35ff77;
          font-weight: 700;
          text-decoration: none;
          transition: color 0.15s ease, text-shadow 0.15s ease;
        }
        .no-tenant__credit a:hover {
          color: #7bff5c;
          text-shadow: 0 0 10px rgba(57, 255, 20, 0.5);
        }
      `}</style>
      <span className="no-tenant__blob no-tenant__blob--a" />
      <span className="no-tenant__blob no-tenant__blob--b" />
      <span className="no-tenant__blob no-tenant__blob--c" />
      <div className="no-tenant__card">
        <div className="no-tenant__mark">MF</div>
        <h1 className="no-tenant__title">Magic Folio</h1>
        {/* Matches the server-rendered /api/web landing (welcome.blade). Absolute
            path to the Laravel backend under /api; guest → SSO, authed → home. */}
        <a className="no-tenant__cta" href="/api/web/login">Login</a>
      </div>
      <p className="no-tenant__credit">
        Made with{" "}
        <span role="img" aria-label="love" style={{ color: "#ff4d6d" }}>
          ♥
        </span>{" "}
        by{" "}
        <a href="https://alicewonders.ws" target="_blank" rel="noopener noreferrer">
          Alice Wonders
        </a>
      </p>
    </div>,
    document.body
  );
}
